import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, Loader2, MapPin, Star, Target } from 'lucide-react';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import {
    Freelancer,
    FreelancerMatch,
    FreelancerQualification,
    formatCents,
    formatDistanz,
    heuteIso,
    suggestFreelancers,
} from '../../services/freelancerLogic';
import { MatchProjektPunkt, fetchProjektPunkte } from '../../services/freelancerService';

// Matching-Vorschau (ERP/CRM-Plan, Abschnitt 7 „Karte + Matching").
//
// Reine VORSCHAU: Projekt waehlen, sehen, wer in Frage kaeme. Die eigentliche
// Freischaltung (`freelancer_project_access`) und der Begehungsauftrag
// entstehen in Phase D2 — hier wird bewusst nichts geschrieben. Der Nutzen
// schon in D1: der Innendienst sieht vor der Akquise eines Auftrags, ob er
// die Begehungen ueberhaupt besetzen kann.
//
// Die Auswahl selbst (wer, warum, in welcher Reihenfolge) trifft
// freelancerLogic.suggestFreelancers — pur und getestet. Diese Komponente
// stellt nur dar.

interface Props {
    freelancers: Freelancer[];
    qualis: FreelancerQualification[];
    /** Sprung in die Akte, wenn ein Nachweis fehlt. */
    onOpenFreelancer?: (f: Freelancer) => void;
}

const Zeile: React.FC<{ match: FreelancerMatch; onOpen?: (f: Freelancer) => void }> = ({ match, onOpen }) => {
    const f = match.freelancer;
    return (
        <li className="border border-slate-200 rounded-2xl p-3 bg-white">
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={() => onOpen?.(f)}
                        className="text-sm font-bold text-slate-800 hover:text-blue-600 text-left truncate"
                    >
                        {f.name}
                    </button>
                    <p className="text-[11px] text-slate-500 truncate">
                        {[f.firma, [f.zip, f.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || '—'}
                    </p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-slate-700 tabular-nums">
                        {match.distanzKm === null ? '—' : formatDistanz(match.distanzKm)}
                    </p>
                    <p className="text-[11px] text-slate-400">Radius {f.einsatzradiusKm} km</p>
                </div>
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                {f.satzJeBegehungCents !== undefined && (
                    <span>{formatCents(f.satzJeBegehungCents)} je Begehung</span>
                )}
                {f.rating !== undefined && (
                    <span className="inline-flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        {f.rating.toFixed(1).replace('.', ',')}
                    </span>
                )}
            </div>

            {match.gruende.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[11px] text-rose-700 list-disc list-inside">
                    {match.gruende.map((g) => (
                        <li key={g}>{g}</li>
                    ))}
                </ul>
            )}
            {match.gruende.length === 0 && match.bereitschaft.warnungen.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[11px] text-amber-700 list-disc list-inside">
                    {match.bereitschaft.warnungen.map((w) => (
                        <li key={w}>{w}</li>
                    ))}
                </ul>
            )}
        </li>
    );
};

export const FreelancerMatchPanel: React.FC<Props> = ({ freelancers, qualis, onOpenFreelancer }) => {
    const [projekte, setProjekte] = useState<MatchProjektPunkt[]>([]);
    const [projektId, setProjektId] = useState('');
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);

    // Laeuft genau einmal (leere Abhaengigkeiten), deshalb steht das
    // Lade-Flag schon im Anfangszustand auf true — ein `setLaedt(true)` im
    // Effektrumpf waere ein zusaetzlicher Renderdurchlauf ohne Wirkung.
    useEffect(() => {
        let abgebrochen = false;
        fetchProjektPunkte()
            .then((p) => {
                if (abgebrochen) return;
                setProjekte(p);
                // Nicht automatisch vorbelegen: ein Vorschlag zu einem
                // Projekt, das niemand gewaehlt hat, wird leicht fuer eine
                // Aussage ueber das gerade offene gehalten.
            })
            .catch((e) => {
                if (!abgebrochen) setFehler(rechtFehlerOder(e));
            })
            .finally(() => {
                if (!abgebrochen) setLaedt(false);
            });
        return () => {
            abgebrochen = true;
        };
    }, []);

    const projekt = useMemo(() => projekte.find((p) => p.id === projektId) ?? null, [projekte, projektId]);

    const heute = heuteIso();
    const ergebnis = useMemo(
        () => (projekt ? suggestFreelancers(projekt, freelancers, qualis, heute) : null),
        [projekt, freelancers, qualis, heute],
    );

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-500 shrink-0" />
                    <h3 className="text-sm font-bold text-slate-700">Passende Freelancer zu einem Projekt</h3>
                </div>

                <select
                    value={projektId}
                    onChange={(e) => setProjektId(e.target.value)}
                    disabled={laedt}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >
                    <option value="">
                        {laedt ? 'Projekte werden geladen…' : '— Projekt wählen —'}
                    </option>
                    {projekte.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.projectNumber ? `${p.projectNumber} · ` : ''}
                            {p.name}
                        </option>
                    ))}
                </select>

                <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                    Nur Projekte mit hinterlegten Koordinaten stehen zur Auswahl — ohne Standort gibt es
                    keine Entfernung. Diese Ansicht ist eine <strong>Vorschau</strong>: die Freischaltung
                    und der Begehungsauftrag folgen in einem späteren Schritt.
                </p>

                {fehler && (
                    <p className="text-[11px] text-rose-700 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                        {fehler}
                    </p>
                )}
            </div>

            {laedt && (
                <p className="py-10 text-center text-sm text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                    Projekte werden geladen…
                </p>
            )}

            {!laedt && !projekt && (
                <p className="py-10 text-center text-sm text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                    Ein Projekt wählen, um Vorschläge zu sehen.
                </p>
            )}

            {projekt && ergebnis && (
                <>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        <span className="truncate">
                            {projekt.name}
                            {projekt.address ? ` · ${projekt.address}` : ''}
                        </span>
                    </div>

                    <section>
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">
                            Vorschläge ({ergebnis.vorschlaege.length})
                        </h4>
                        {ergebnis.vorschlaege.length === 0 ? (
                            <p className="py-6 text-center text-sm text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                                {ergebnis.projektUnverortet
                                    ? 'Das Projekt ist nicht verortet — ohne Koordinaten lässt sich keine Entfernung berechnen.'
                                    : 'Niemand ist einsatzbereit und zugleich im Einsatzradius.'}
                            </p>
                        ) : (
                            <ul className="space-y-2">
                                {ergebnis.vorschlaege.map((m) => (
                                    <Zeile key={m.freelancer.id} match={m} onOpen={onOpenFreelancer} />
                                ))}
                            </ul>
                        )}
                    </section>

                    {ergebnis.ausgeschlossen.length > 0 && (
                        <section>
                            <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">
                                Nicht vorgeschlagen ({ergebnis.ausgeschlossen.length})
                            </h4>
                            {/* Bewusst mit Begruendung statt stillschweigend weggelassen:
                                „warum steht Herr M. nicht dabei?" ist die erste Frage,
                                die hier gestellt wird. */}
                            <ul className="space-y-2 opacity-90">
                                {ergebnis.ausgeschlossen.map((m) => (
                                    <Zeile key={m.freelancer.id} match={m} onOpen={onOpenFreelancer} />
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            )}
        </div>
    );
};
